/**
 * @Author: v_jppjpeng
 * @Date: 2023-05-17 16:45:48
 * @Explain: 业务逻辑
 */
import { intlGet } from '@/locales';
import moment from 'moment';
import { getDvaApp } from 'umi';
// router
import RouterPath from '@/router/routingPath';
// utils
import { getActivityState } from '@/pages/activityManage/utils/const/list';
import { formatDateTime, getSystemTimezone } from 'smartlink-format-time';
import { compareNum, dividedBy, minusBy, multipliedBy } from '../format/number';
import { formattedTime, getDateTimeFormat, getTimeStamp } from '../format/time';
import { getUrlParams, onSerialize } from '../format/url';
import { isArrayLen, isEmptyObject, isNumber } from './dataJudgment';
// type
import type {
  BusinessListItemIF,
  ProjectApplyInfoIF,
  UsedStatusType,
} from '@/api/common/data.d';
import { usedStatusTypeEnum } from '@/api/common/data.d';
import type { TemplateSelectItem } from '@/components/common/templateSelect';
import {
  ActionTypeEnum,
  BooleanTypeEnum,
  OperationEnum,
  StatusTipEnum,
} from '@/definition/common/data.d';
import { LayoutTypeEnum } from '@/layouts/data.d';
import type { MailContentItem } from '@/pages/activityManage/create/data.d';
import { ActivityStatus } from '@/pages/activityManage/utils/typing/list';
import { AfterLoginRenderTypeEnum } from '@/pages/adminManage/data.d';
import { AutomatedStatusEnum } from '@/pages/userJourney/utils/enum/list';
// store
import type { UnSearchInfoType } from '@/hooks/useDataList';
import type { ConnectState } from '@/models/connect.d';
import type { JumpSource } from '@/pages/mailTemplate/data.d';

/**
 * 格式化时间展示，接口返回的是utc+8的时间，转换成用户设置的时区时间
 * str - string: 有效日期时间格式 number： 毫秒
 **/
export const formatShowTime = (
  str: string | number,
  config?: {
    placeholder?: string;
    /** 用户期望的时间格式化效果 */
    dateFormat?: string;
    /** 是否需要根据时区转换 */
    isConvertTimeZone?: boolean;
    optsInputTimeZone?: string;
    optsTargetTimeZone?: string;
  },
) => {
  const {
    dateFormat,
    placeholder = '-',
    isConvertTimeZone,
    optsInputTimeZone,
    optsTargetTimeZone,
  } = config || {};

  let timeStr = formatDateTime({
    dateVal: str,
    isConvertTimeZone,
    inputTimeZone: optsInputTimeZone,
    targetTimeZone: optsTargetTimeZone,
    dateFormat: dateFormat || getDateTimeFormat().showFormatAmPm,
  });
  // 使用默认12小时制格式化时间则：处理am、pm中文环境展示为上午、下午
  if (!dateFormat && timeStr) {
    timeStr = formattedTime(timeStr);
  }
  return timeStr ? timeStr : placeholder;
};
/**
 * 接口需要的时间格式
 * str - string: 有效日期时间格式 number： 毫秒
 **/
export const onInterfaceTime = (
  str: string | number,
  config?: {
    dateFormat?: string;
    isConvertTimeZone?: boolean;
    optsInputTimeZone?: string;
    optsTargetTimeZone?: string;
  },
) => {
  const {
    dateFormat,
    isConvertTimeZone,
    optsInputTimeZone,
    optsTargetTimeZone,
  } = config || {};
  return formatDateTime({
    dateVal: str,
    isConvertTimeZone,
    inputTimeZone: optsInputTimeZone,
    targetTimeZone: optsTargetTimeZone,
    dateFormat: dateFormat || getDateTimeFormat().interfaceFormat,
  });
};
/**
 * 接口需要的时间格式
 * str - string: 有效日期时间格式 number： 毫秒
 **/
export const onInterfaceTimeNew = (
  str: string | number,
  config?: {
    dateFormat?: string;
    isConvertTimeZone?: boolean;
    optsInputTimeZone?: string;
    optsTargetTimeZone?: string;
    /** 拼接 */
    splicingTimezone?: string;
  },
) => {
  const {
    dateFormat,
    isConvertTimeZone,
    optsInputTimeZone,
    optsTargetTimeZone,
    splicingTimezone = getSystemTimezone(),
  } = config || {};
  const timeVal = formatDateTime({
    dateVal: str,
    isConvertTimeZone,
    inputTimeZone: optsInputTimeZone,
    targetTimeZone: optsTargetTimeZone,
    dateFormat: dateFormat || getDateTimeFormat().interfaceFormatNew,
  });

  return timeVal ? timeVal + splicingTimezone : '';
};

// 清除returnUrl链接上带的access_token、refresh_token、user
export const onClearLinkToken = (url: string) => {
  const regex = /(.*?)\?/;
  const matches = url.match(regex);
  const domain = matches?.[1] || url;
  const params = getUrlParams(url);
  delete params.access_token;
  delete params.refresh_token;
  delete params.user;
  return isEmptyObject(params) ? domain : `${domain}?${onSerialize(params)}`;
};

// 判断是否是新用户、是否拥有项目，跳转对应的欢迎页
export const getWelcomePageUrl = (
  data: {
    isNewUser?: boolean;
    hasProject?: boolean;
    isAcceptedInvitation?: boolean;
  },
  projectApplyInfo: ProjectApplyInfoIF,
  businessList?: BusinessListItemIF[],
) => {
  // 是否新用户 - 默认true
  const isNewUser = data?.isNewUser;
  // 是否拥有项目 - 默认false
  const isProject = !data?.hasProject;
  // 是否接受邀请 - 默认false
  const isAcceptedInvitation = !data?.isAcceptedInvitation;
  let to = '';
  const query = {
    ...projectApplyInfo,
    renderType: '',
    layoutType: '',
  };
  if (
    (isNewUser && query.invitationCode) ||
    (query.invitationCode && !businessList?.length)
  ) {
    // 新用户 - 邀请加入项目 或 用户通过邀请链接进入且用户无业务
    to = RouterPath.WELCOME_PAGE;
    query.renderType = AfterLoginRenderTypeEnum.NEW_USER;
    query.layoutType = LayoutTypeEnum.FULL_SCREEN;
  } else if (query.invitationCode && isAcceptedInvitation) {
    // 不是新用户 - 邀请加入项目
    to = RouterPath.HOME;
    query.renderType = AfterLoginRenderTypeEnum.INVITER_JOIN_PROJECT;
  } else if (isProject) {
    // 未加入项目
    to = RouterPath.WELCOME_PAGE;
    query.renderType = AfterLoginRenderTypeEnum.NOT_JOIN_PROJECT;
    query.layoutType = LayoutTypeEnum.FULL_SCREEN;
  } else if (window.location.pathname === '/') {
    to = RouterPath.HOME;
  }

  return {
    to,
    query,
  };
};

// 创建活动 - 是否开启模板验证
export const isTempVerify = (data: MailContentItem, type: string = 'all') => {
  let flag = false;
  const {
    placeholder = [],
    // 联系人占位符 没有默认值
    contactPlaceholder = [],
    hasUnsubscribeOrPreferencesLink,
    isUnsubscribed,
    templateDeleted,
    unactiveForm,
    hasTestLink,
  } = data;

  /**
   * 1.未创建占位符
   * 2.未激活的表单
   **/
  if (isArrayLen(placeholder) || isArrayLen(unactiveForm)) {
    flag = true;
  }

  /**
   * 1.未配置偏好链接
   * 2.未配置取消订阅链接
   * 3.模板被删除
   * 4.未添加测试链接（仅在 testerManage 页面检查）
   */
  // 是否满足"退订/偏好设置"基础条件
  const lacksUnsubscribeBasicConditions = !(
    hasUnsubscribeOrPreferencesLink || isUnsubscribed
  );

  // 未添加测试链接 && 在 testerManage 页面
  if (lacksUnsubscribeBasicConditions || templateDeleted) {
    flag = true;
  }
  return flag;
};
// 创建活动 - 获取模板验证状态
export const getTempVerifyStatus = (
  item: MailContentItem,
  isAbTest?: boolean,
) => {
  let status = StatusTipEnum.empty;
  if (isAbTest) {
    if (+item.templateId > 0) {
      status = isTempVerify(item, 'change')
        ? StatusTipEnum.error
        : StatusTipEnum.success;
    }
  } else {
    // 主题存在内容 且 主题携带未创建的属性
    const subjectHasUnCreateAttr =
      item.subject && !!item?.subjectUncreatedAttrs?.length;

    if (subjectHasUnCreateAttr) {
      status = StatusTipEnum.error;
    } else if (
      item.subject &&
      +item.templateId > 0 &&
      !isTempVerify(item, 'change')
    ) {
      status = StatusTipEnum.success;
    } else if (+item.templateId > 0 && isTempVerify(item, 'change')) {
      status = StatusTipEnum.error;
    } else if (
      (!item.templateId && item.subject) ||
      (item.templateId && !item.subject)
    ) {
      status = StatusTipEnum.error;
    }
  }

  return status;
};

// 格式化 - 来源名称 (来源为API-xxx, 则删除前缀API-)
export const formatSourceName = (str: string) =>
  str?.includes('API-') ? str.replace('API-', '') : str;

// 查询字符串是否含有指定前缀信息,并剔除前缀
// 可用于格式化API来源名称(API-)、表单来源名称(Form-)的前缀剔除
export const delMatchPrefix = ({
  text,
  targetPrefix,
  compareNoCase,
}: {
  // 数据源
  text: any;
  // 目标前缀
  targetPrefix: string;
  // 是否开启模糊匹配大小写
  compareNoCase?: boolean;
}) => {
  if (typeof text !== 'string' || typeof targetPrefix !== 'string') {
    return text;
  }
  const newText = compareNoCase ? text.toLocaleLowerCase() : text;
  const newTargetPrefix = compareNoCase
    ? targetPrefix.toLocaleLowerCase()
    : targetPrefix;
  // 数据源是否包含目标前缀
  const isHas = newText.indexOf(newTargetPrefix) !== -1;
  // 未匹配到目标前缀,原样返回
  if (!isHas) {
    return text;
  }
  // 匹配到目标前缀,返回删除前缀后信息
  if (isHas) {
    return text.slice(targetPrefix.length, text.length);
  }
};

/** 由发件地址前缀、发件地址后缀 拼接邮箱地址 */
export const concatEmailAddress = ({
  addressPrefix,
  domain,
}: {
  addressPrefix?: string;
  domain?: string;
}) => `${addressPrefix || '-'}${domain ? `@${domain}` : '-'}`;

/** 是否从gcs 获取 */
export const getGcsFlag = (time: any) => {
  const currentTime = 1714123206911;
  const reducedTime = time && getTimeStamp(time);
  if (reducedTime && reducedTime < currentTime) {
    return false;
  }
  return true;
};

/** 把默认语言插到第一位 --- 因为是根据字母排序的，所以把默认语言设置成a，其他恢复原状 */
export const onTempDefaultLangInsertFirst = (
  list: any[],
  defaultLang: string,
) => {
  return list.map((item) => {
    if (item.group === defaultLang) {
      return {
        ...item,
        sortKey: '0',
      };
    }
    return item;
  });
};

/** 操作按钮禁用逻辑 - 涉及类型定义 */
export type ReferenceDisableParamsType = {
  /** 禁用信息 */
  usedStatus: UsedStatusType[];
  /** 操作项名称 */
  operationName?: string;
  /** 禁用状态code集合；提示优先级（"线上活动引用" > "api引用" > "激活表单引用" > "自动化活动"） */
  disPriority?: usedStatusTypeEnum[];
  /** 操作类型 */
  action?: (OperationEnum | 'rename' | string)[];
};
export type ReferenceDisableResultsType = {
  disabled: boolean; // 是否禁用
  msg: string; // 禁用提示文案
  quotaType: number; // 应用类型
  usedStatus: ReferenceDisableParamsType['usedStatus']; // 按照禁用优先级返回入参中的禁用信息
  referenceProhibition?: { [key: OperationEnum | 'rename' | string]: string }; // {name}引用中，不可{do}
  allQuoteResInfo?: {
    disabled: boolean; // 是否禁用
    msg: string; // 禁用提示文案
    quotaType: number; // 应用类型
    referenceProhibition?: { [key: OperationEnum | 'rename' | string]: string }; // {name}引用中，不可{do}
  }[]; // 收集所有引用结论信息
};
// 获取引用项信息
export const getQuoteRes = ({
  disPriorityItem,
  operationName,
  usedStatusItem,
}: {
  disPriorityItem: number;
  operationName?: string;
  usedStatusItem: UsedStatusType;
}) => {
  let who = '';
  let result = { disabled: false, msg: '' };
  switch (disPriorityItem) {
    // 被线上活动引用
    case usedStatusTypeEnum.THREE:
      who = intlGet('COMMON.CAMPAIGN');
      // 默认被常规、周期、ab活动引用提示文案
      let threeMsg = intlGet('API.ERROR.CODE.00003', {
        name: operationName,
      });
      // 判断活动状态是否为草稿
      const isDraftCampaign =
        isNumber(usedStatusItem.campaignState) &&
        getActivityState(usedStatusItem.campaignState) ===
          ActivityStatus.unpublished;
      // 被草稿状态活动引用更改默认提示文案
      if (isDraftCampaign) {
        threeMsg = intlGet('API.ERROR.CODE.00007');
      }
      result = { disabled: true, msg: threeMsg };
      break;
    // 被API引用
    case usedStatusTypeEnum.ONE:
      who = 'API';
      result = {
        disabled: true,
        msg: intlGet('API.ERROR.CODE.10356'),
      };
      break;
    // 被激活状态的表单引用
    case usedStatusTypeEnum.TWO:
      who = intlGet('COMMON.FORM');
      result = {
        disabled: true,
        msg: intlGet('API.ERROR.CODE.00002'),
      };
      break;
    // 被自旅程（即原：动化活动）引用
    case usedStatusTypeEnum.FOUR:
      who = intlGet('LEFT.MENU.EVENT6');
      // 默认被旅程引用提示文案
      let fourMsg = intlGet('API.ERROR.CODE.00005');
      // 判断旅程状态是否为草稿
      const isDraftJourney =
        usedStatusItem.campaignState === AutomatedStatusEnum.NOT_ACTIVITE;
      // 被草稿状态旅程引用更改默认提示文案
      if (isDraftJourney) {
        fourMsg = intlGet('API.ERROR.CODE.00007');
      }
      result = { disabled: true, msg: fourMsg };
      break;
    // 被模板引用
    case usedStatusTypeEnum.FIVE:
      who = intlGet('LEFT.MENU.TEMPLATE1');
      result = {
        disabled: true,
        msg: intlGet('API.ERROR.CODE.00006'),
      };
      break;
  }

  return {
    ...result,
    who,
  };
};
// 获取引用像具体到操作按钮上禁用文案(根据具体操作类型给出对应文案： {who}引用中，不可{doText})
export const getActionQutoRes = ({
  who,
  action,
}: {
  who: string;
  action: ReferenceDisableParamsType['action'];
}) => {
  let referenceProhibition = {};
  action?.map((item) => {
    let doText = '';
    switch (item) {
      case OperationEnum.EDIT:
        doText = intlGet('COMMON.EDIT');
        break;
      case OperationEnum.DEL:
        doText = intlGet('COMMON.DELETE');
        break;
      case 'rename':
        doText = intlGet('COMMON.RENAME');
        break;
    }
    if (who && doText) {
      const referenceProhibitionText = intlGet('API.ERROR.CODE.00001', {
        who: who.toLocaleLowerCase(),
        doText: doText.toLocaleLowerCase(),
      });
      referenceProhibition = {
        ...referenceProhibition,
        [item]: referenceProhibitionText,
      };
    }
  });
  return referenceProhibition;
};
// 活动禁用优先级
const disPriorityData = [
  usedStatusTypeEnum.THREE,
  usedStatusTypeEnum.ONE,
  usedStatusTypeEnum.TWO,
  usedStatusTypeEnum.FOUR,
  usedStatusTypeEnum.FIVE,
];
/** 操作按钮禁用逻辑 */
export const referenceDisable = ({
  usedStatus,
  action = [],
  operationName = '',
  disPriority = disPriorityData,
}: ReferenceDisableParamsType): ReferenceDisableResultsType => {
  let result: ReferenceDisableResultsType = {
    disabled: false,
    msg: '',
    quotaType: 0,
    usedStatus: [],
    allQuoteResInfo: [],
    referenceProhibition: {},
  };
  // 按照禁用优先级顺序收集当前所有禁用的信息
  const allQuoteResInfo = [];
  let who = '';
  // 标识已收集最高禁用优先级
  let isHighest = false;
  const newUsedStatus: ReferenceDisableParamsType['usedStatus'] = [];
  for (let i = 0; i < disPriority.length; i++) {
    const usedStatusItem = usedStatus.find(
      (v) => disPriority[i] === v.quotaType,
    );
    if (usedStatusItem) {
      newUsedStatus.push(usedStatusItem);
      // 获取引用项信息
      const quoteRes = getQuoteRes({
        disPriorityItem: disPriority[i],
        operationName,
        usedStatusItem,
      });
      // 未收集最高禁用优先级时则收集
      if (!isHighest) {
        isHighest = true;
        who = quoteRes.who;
        result = {
          ...result,
          msg: quoteRes.msg,
          quotaType: disPriority[i],
          disabled: quoteRes.disabled,
        };
      }
      // 获取引用像具体到操作按钮上禁用文案(根据具体操作类型给出对应文案： {who}引用中，不可{doText})
      const referenceProhibition = getActionQutoRes({
        action,
        who: quoteRes.who,
      });
      allQuoteResInfo.push({
        who: quoteRes.who,
        msg: quoteRes.msg,
        referenceProhibition,
        quotaType: disPriority[i],
        disabled: quoteRes.disabled,
      });
    }
  }

  // 根据具体操作类型给出对应文案： {who}引用中，不可{doText}
  const referenceProhibition = getActionQutoRes({ who, action });

  result = {
    ...result,
    referenceProhibition,
    allQuoteResInfo,
    usedStatus: newUsedStatus,
  };

  return result;
};
// 活动联动禁用优先级排序
export const referencePrioritySorting = ({
  usedStatus,
}: {
  usedStatus: ReferenceDisableParamsType['usedStatus'];
}) => {
  const sortResult: UsedStatusType[] = [];
  disPriorityData.map((item: number) => {
    const obj: UsedStatusType | undefined = usedStatus?.find(
      (i) => i.quotaType === item,
    );
    if (obj) sortResult.push(obj);
  });
  return sortResult;
};
/** 获取禁用为true的提示语逻辑的 */
export const getDisabledConfig = (
  data: { disabled?: boolean; message: string }[],
) => {
  let disabled = false;
  let message = '';
  for (const item of data) {
    if (item.disabled) {
      disabled = true;
      message = item.message;
      break;
    }
  }
  return {
    disabled,
    message,
  };
};

// 参与度文案 高、中、低
export const getRatingText = ({ userGrade }: any) => {
  let ratingText = '';
  switch (userGrade) {
    case 3:
      ratingText = intlGet('CONTACT.MANAGE.LIST.HIGH');
      break;
    case 2:
      ratingText = intlGet('CONTACT.MANAGE.LIST.MEDIUM');
      break;
    case 1:
      ratingText = intlGet('CONTACT.MANAGE.LIST.LOW');
      break;
  }

  return ratingText;
};

// 指定class下的input，focus
export const onAssignedFocus = (className: string) => {
  if (className) {
    const dom = document.querySelector(className)?.querySelector('input');

    console.log('dom', dom);

    dom?.focus();
  }
};

// 获取删除后获取列表数据页码
export const getDelAfterPageIndex = ({
  currentUrl,
  recordCount,
  delNum = 1,
}: {
  currentUrl: string;
  recordCount: number;
  delNum?: number; // 删除条数 默认删除1条
}) => {
  const { getState } = getDvaApp()._store;
  const { CommonModel }: ConnectState = getState();
  const { filterList = {} } = CommonModel || {};
  const { pageIndex = 1, pageSize = 10 } = filterList?.[currentUrl] || {};

  // 最后一页码数
  const lastPageIndex = Math.ceil(dividedBy(recordCount, pageSize));
  // 上一页码数
  const previous = minusBy(pageIndex, 1);
  // 最后一页数据条数
  let numberOfLastPages = 0;
  // 存在上一页数据
  if (previous > 0) {
    numberOfLastPages = minusBy(recordCount, multipliedBy(previous, pageSize));
  } else {
    numberOfLastPages = recordCount;
  }

  // 删多条数据
  /**
   * 1、当处于第X页多选删除数据时，若删除后剩余数据不满足X页，可以回到最后1页
   * 2、当处于第X页多选删除数据时，若删除后剩余数据满足X页，可以停在第X页
   */
  if (delNum > 1) {
    const delAterRecordCount = minusBy(recordCount, delNum);
    delAterRecordCount >= 0 ? delAterRecordCount : 0;
    const delAterLastPageIndex = Math.ceil(
      dividedBy(delAterRecordCount, pageSize),
    );
    // 若删除后剩余数据不满足X页，可以回到最后1页
    if (compareNum(delAterLastPageIndex, pageIndex) === -1) {
      return { pageIndex: delAterLastPageIndex };
    }

    return;
  }

  // 删一条数据
  // 删除的最后一页&删除后此页将无数据 -> 则列表获取数据页码需前置1页（若最后一页即是首页则无需前置）
  if (
    filterList.hasOwnProperty(currentUrl) &&
    lastPageIndex === pageIndex &&
    numberOfLastPages === delNum &&
    delNum === 1
  ) {
    const newPageIndex = minusBy(pageIndex, 1);
    return {
      pageIndex: newPageIndex >= 1 ? newPageIndex : 1,
    };
  }
  return;
};

/** 跳转、生成审批详情 */
export const getApprovalDetailsParams = (
  item: { id?: any; uuid?: any; qpsType: any; isExpNewVersion?: boolean },
  config: {
    jumpSource: JumpSource | string;
    redirectLink?: string;
    gameId?: any;
  },
) => {
  const { jumpSource, redirectLink, gameId } = config || {};
  let pageUrl = RouterPath.ACTIVITY_APPROVAL_DETAILS;
  const linkParams: any = {
    action: ActionTypeEnum.DETAILS,
    // 是否展示返回按钮
    isShowBack: BooleanTypeEnum.Y,
    // 从哪里条过来
    source: jumpSource,
    // 活动类型
    qpsType: item?.qpsType,
  };
  if (item.id) {
    linkParams.id = item.id;
  }
  if (item.uuid) {
    linkParams.uuid = item.uuid;
  }
  // 回退地址
  if (redirectLink) {
    linkParams.redirectLink = redirectLink;
  }
  // 业务id
  if (gameId) {
    linkParams.gameId = gameId;
  }
  // 是否新ab测试
  if (item.isExpNewVersion === true) {
    pageUrl = RouterPath.ACTIVITY_AB_TEST_APPROVAL_DETAILS;
  }

  return {
    pageUrl,
    linkParams,
  };
};

// 获取列表过程中增删接口是否查询字段
export const processGetListUnSearchInfo = ({
  searchParams,
  type,
  addUnSearchInfo,
  delUnSearchKey,
}: {
  searchParams: any;
  type: 'delUnSearch' | 'addUnSearch';
  addUnSearchInfo?: UnSearchInfoType;
  delUnSearchKey?: string;
}) => {
  // 添加不搜索信息
  if (type === 'addUnSearch' && addUnSearchInfo) {
    const unSearchInfo = [...(searchParams?.unSearchInfo || [])];

    // 查找记录的相同key
    const sameIndex = unSearchInfo.findIndex(
      (item: UnSearchInfoType) => item.key === addUnSearchInfo.key,
    );
    if (sameIndex === -1) {
      unSearchInfo.push(addUnSearchInfo);
    }

    return {
      ...searchParams,
      unSearchInfo,
    };
  }

  // 移除
  if (type === 'delUnSearch' && delUnSearchKey) {
    // 记录中被移除的字段默认筛选信息
    let delSearchInfo = {};
    // 不传递至接口的key
    const unSearchInfo: UnSearchInfoType[] = [];

    (searchParams?.unSearchInfo || [])?.map((item: UnSearchInfoType) => {
      if (item.key !== delUnSearchKey) {
        unSearchInfo.push(item);
      } else {
        delSearchInfo = { ...delSearchInfo, [item.key]: item.defaultValue };
      }
    });

    return {
      ...searchParams,
      ...delSearchInfo,
      unSearchInfo,
    };
  }

  return searchParams;
};

// 初始化名称
export const getUntitledName = () =>
  `Untitled ${formatShowTime(new Date(), {
    isConvertTimeZone: false,
  })}`;

/**
 * 递归地从嵌套对象结构中提取指定键名下的所有数组内容。
 * @param list 当前层级的节点数组。
 * @param key 需要整合的数组的键名。
 * @returns 一个包含所有指定数组元素的新数组。
 */
export function deepFlattenByKey<
  T extends { children?: T[] },
  K extends keyof T,
>(list: T[], key: K): T[K] extends (infer U)[] ? U[] : never {
  const result: any[] = [];
  for (const node of list) {
    // 1. 处理当前节点的 key
    const valueToFlatten = node[key];
    if (Array.isArray(valueToFlatten)) {
      result.push(...valueToFlatten);
    }
    // 2. 递归处理子节点
    if (node.children && node.children.length > 0) {
      const childResult = deepFlattenByKey(node.children, key);
      result.push(...childResult);
    }
  }
  return result as any;
}

/** 将时间格式化精确到 秒
 * 兼容 日期字符串 以及时间戳类型
 * return YYYY-MM-DD HH:mm:ss
 * **/

export const formatTimestamp = (timestamp?: number | string) => {
  if (!timestamp) return '';

  // 如果是数字或数字字符串，当作 Unix 时间戳处理
  const num = Number(timestamp);
  if (!isNaN(num)) {
    // 检查是秒还是毫秒（大于 10^10 的是毫秒）
    const timestamp = num > 9999999999 ? num : num * 1000;
    return moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
  }

  // 否则当作日期字符串处理
  return moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
};

// 更换模板同步获取模板携带的主题、预览文本、主题携带属性信息
export const getTemplateSyncUpdateSUbjectData = ({
  templateId,
  options,
}: {
  templateId: number;
  options: TemplateSelectItem[];
}) => {
  const defaultSublectInfo = {
    // 主题内容
    subject: '',
    // 预览文本
    previewText: '',
    // 主题携带的用户属性&属性默认值
    subjectAttributesDefaultValue: {},
  };
  // 默认主题相关信息
  const sameTemplateItem = options.find((item) => item.value === templateId);
  // 无匹配模板 或 清空模板行为：清空相关信息
  if (!templateId || !sameTemplateItem?.subjectInfo || isNaN(templateId)) {
    return defaultSublectInfo;
  }
  const subjectData = {
    subject: sameTemplateItem?.subjectInfo?.subject || '',
    previewText: sameTemplateItem?.subjectInfo?.previewText || '',
    subjectAttributesDefaultValue:
      sameTemplateItem?.subjectInfo?.subjectAttributesDefaultValue || {},
  };
  return subjectData;
};
